import { useFeatureAccess } from "@/app/hooks/useFeatureAccess";
import { format } from "date-fns";
import { useForm } from "react-hook-form";
import { useNavigate } from "react-router";
import UnifiedFilters from "./FilterComponent";
import { useUtils } from "@/app/hooks/useUtils";
import { useLoader } from "@/app/hooks/useLoader";
import { useRouter } from "@/app/hooks/useRouter";
import useRouteInfo from "@/app/hooks/useRouteInfo";
import { useEffect, useState, useRef, useMemo } from "react";
import PetName from "@/app/components/ui/Reusable/PetName";
import Username from "@/app/components/ui/Reusable/Username";
import TableComponent, { type TableColumn } from "@/app/components/ui/Reusable/TableComponent";
import { useImageConstants } from "@/app/hooks/useImageConstants";
import { useModalActions } from "@/app/hooks/ui_hooks/useModalActions";
import BookingStatus from "@/app/components/ui/Reusable/BookingStatus";
import TableSkeletonComponent from "@/app/components/ui/LoaderComponent";
import { IBookingCancelationModalState } from "@components/routes/modals/Cancelation";
import { useAppDispatch, useAppSelector } from "@/app_redux/hooks/root_hook";
import { default as NORecordFoundComponent } from "@/app/components/ui/NoRecordFound";
import { useOrgServicesListAPI } from "@/app/hooks/api_hooks/org/useOrgServicesListAPI";
import { useShiftBookingListAPI } from "@/app/hooks/api_hooks/bookings/useShiftBookingListAPI";
import { setSlotBookingFilters, setSlotsList } from "@/app_redux/reducers/slices/auth/slots_slice";
import { useShiftedBookingsCountAPI } from "@/app/hooks/api_hooks/bookings/useShiftBookingsCountAPI";
interface ICityClinicFormFields {
  city_id: string;
  clinic_id: string;
  clinic_name: string;
  service_id: string[];
  subservice_id: string[];
}

const AppointmentShiftAppointment = () => {
  const { NO_PWL_RESULTS } = useImageConstants();
  const { veticDateFormat, getDisplayStaffName } = useUtils();
  const { getRouteKey } = useRouter();
  const routeId = getRouteKey("SLOTS", "id");
  const { routeState } = useRouteInfo(routeId);
  const { shiftAppoitnemt } = routeState.state as any;
  const isShiftListLoading = useLoader("isShiftAppointmentInProgress");
  const { clinics, cities } = useAppSelector((state) => state.root.UIStates);
  const { org_services: orgServices } = useAppSelector((state) => state.staticAPI);
  const { setVisible: setShow, setModalData: setModal } = useModalActions("APPOINTMENT_CANCELATION");
  // Front desk overbooking users cannot reschedule
  const isFrontDeskOverbooking = useFeatureAccess("FRONT_DESK_OVERBOOKING");
  const { setVisible: setReschedule, setModalData: setRescheduleData } =
    useModalActions("APPOINTMENT_RESCHEDULE_SHIFT");
  const fetchShiftAppointment = useShiftBookingListAPI();
  const { execute: fetchShiftAppointmentCount, countData: shiftAppointmentCount } = useShiftedBookingsCountAPI();
  const fetchOrgServices = useOrgServicesListAPI();
  const [selectedRange, setSelectedRange] = useState<string>(format(new Date(), "yyyy-MM-dd"));
  const [selectedFromDate, setSelectedFromDate] = useState<string>("");
  const [selectedToDate, setSelectedToDate] = useState<string>("");
  const [selectedClinicId, setSelectedClinicId] = useState<string>("");
  const [selectedCityId, setSelectedCityId] = useState<string>("");
  const [includeTimestamp, setIncludeTimestamp] = useState<boolean>(true);
  const [selectedServiceIds, setSelectedServiceIds] = useState<string[]>([]);
  const [selectedSubServiceIds, setSelectedSubServiceIds] = useState<string[]>([]);
  const [applyFiltersTrigger, setApplyFiltersTrigger] = useState<number>(0);
  const isInitialMount = useRef<boolean>(true);
  const prevFilters = useRef<string>("");
  const lastChangeSource = useRef<"quick" | "custom" | "other" | null>(null);
  const prevApplyFiltersTrigger = useRef<number>(0);
  const navigate = useNavigate();
  const dispatch = useAppDispatch();
  const { getCurrentDate } = useUtils();
  const queryParams = new URLSearchParams(location.search);
  const time = queryParams.get("t") ?? "";
  const prevTime = useRef<string>(time);
  const hasFetchedShiftServicesRef = useRef<boolean>(false);

  const formHook = useForm<ICityClinicFormFields>({
    mode: "onChange",
    defaultValues: {
      city_id: "",
      clinic_id: "",
      clinic_name: "",
      service_id: [],
      subservice_id: [],
    },
  });

  const shouldMakeAPICall = () => {
    if (selectedRange && !selectedFromDate && !selectedToDate) {
      return true;
    }
    if (selectedFromDate && selectedToDate) {
      return true;
    }
    if ((selectedFromDate && !selectedToDate) || (!selectedFromDate && selectedToDate)) {
      return false;
    }
    return false;
  };

  const buildFilterParams = () => {
    const params: any = {};
    if (selectedCityId) {
      if (selectedCityId === "-1") {
        const allCityIds = cities.map((city) => city.id).filter((id) => id !== "-1");
        params.city_id = allCityIds;
      } else {
        params.city_id = [selectedCityId];
      }
    }
    if (selectedClinicId) {
      if (selectedClinicId === "-1") {
        const allClinicIds = clinics.map((clinic) => clinic.id).filter((id) => id !== "-1");
        params.clinic_id = allClinicIds;
      } else {
        params.clinic_id = [selectedClinicId];
      }
    }
    if (selectedServiceIds.length > 0) {
      params.service_id = selectedServiceIds;
    }
    if (selectedSubServiceIds.length > 0) {
      params.subservice_id = selectedSubServiceIds;
    }
    if (selectedFromDate && selectedToDate) {
      params.from_date = selectedFromDate;
      params.to_date = selectedToDate;
    } else if (selectedRange) {
      params.date = selectedRange;
      if (includeTimestamp) {
        const now = new Date();
        const istString = now.toLocaleString("en-US", {
          timeZone: "Asia/Kolkata",
        });
        const istDate = new Date(istString);
        params.time = Math.floor(istDate.getTime() / 1000);
      }
    }
    return params;
  };

  const getFiltersKey = (): string => {
    return JSON.stringify({
      selectedCityId,
      selectedClinicId,
      selectedServiceIds,
      selectedSubServiceIds,
      selectedRange,
      selectedFromDate,
      selectedToDate,
      includeTimestamp,
    });
  };

  useEffect(() => {
    if (hasFetchedShiftServicesRef.current) return;
    hasFetchedShiftServicesRef.current = true;
    fetchOrgServices();
  }, [fetchOrgServices]);

  useEffect(() => {
    const currentFiltersKey = getFiltersKey();
    const shouldFetchData = shouldMakeAPICall();
    const timeChanged = time !== prevTime.current;
    const applyClicked = applyFiltersTrigger !== prevApplyFiltersTrigger.current;
    const isQuickFilterChange = lastChangeSource.current === "quick";
    if (isInitialMount.current) {
      if (shouldFetchData) {
        const initialFilters = buildFilterParams();
        fetchShiftAppointment(initialFilters);
        fetchShiftAppointmentCount(initialFilters);
        prevFilters.current = currentFiltersKey;
      }
      isInitialMount.current = false;
      prevTime.current = time;
      prevApplyFiltersTrigger.current = applyFiltersTrigger;
      return;
    }
    if (!shouldFetchData) {
      prevTime.current = time;
      prevApplyFiltersTrigger.current = applyFiltersTrigger;
      return;
    }
    if (!timeChanged && !isQuickFilterChange && !applyClicked) return;
    if (prevFilters.current === currentFiltersKey && !timeChanged && !applyClicked) return;
    const filters = buildFilterParams();
    fetchShiftAppointment(filters);
    fetchShiftAppointmentCount(filters);
    prevFilters.current = currentFiltersKey;
    prevTime.current = time;
    prevApplyFiltersTrigger.current = applyFiltersTrigger;
    lastChangeSource.current = null;
  }, [
    time,
    applyFiltersTrigger,
    selectedClinicId,
    selectedRange,
    selectedFromDate,
    selectedToDate,
    selectedCityId,
    selectedServiceIds,
    selectedSubServiceIds,
    includeTimestamp,
    fetchShiftAppointment,
    fetchShiftAppointmentCount,
  ]);

  const handleCityChange = (cityId: string) => {
    lastChangeSource.current = "other";
    setSelectedCityId(cityId);
    setSelectedClinicId("");
  };

  const handleClinicChange = (clinicId: string) => {
    lastChangeSource.current = "other";
    setSelectedClinicId(clinicId);
  };

  const handleServiceChange = (serviceIds: string[]) => {
    lastChangeSource.current = "other";
    setSelectedServiceIds(serviceIds);
  };

  const handleSubServiceChange = (subServiceIds: string[]) => {
    lastChangeSource.current = "other";
    setSelectedSubServiceIds(subServiceIds);
  };

  const handleDateRangeChange = (range: string, isToday: boolean) => {
    lastChangeSource.current = "quick";
    setSelectedRange(range);
    setSelectedFromDate("");
    setSelectedToDate("");
    setIncludeTimestamp(isToday);
  };

  const handleCustomDateRangeChange = (fromDate: string, toDate: string) => {
    lastChangeSource.current = "custom";
    setSelectedFromDate(fromDate);
    setSelectedToDate(toDate);
    setSelectedRange("");
    setIncludeTimestamp(false);
  };

  const handleApplyFilters = () => {
    lastChangeSource.current = "other";
    setApplyFiltersTrigger((prev) => prev + 1);
  };

  const handleModifyAppointment = (bookingDetails: any) => {
    dispatch(
      setSlotsList({
        data: [],
      }),
    );
    dispatch(
      setSlotBookingFilters({
        filters: {
          booking_date: getCurrentDate(),
          clinic_ids: [],
          location_details: {
            latitude: "",
            longitude: "",
          },
          service_id: "",
          subservice_id: "",
          slot_status: "",
          staff_ids: [],
          pet_species_id: "",
          city_id: "",
        },
      }),
    );
    setReschedule();
    setRescheduleData({
      userId: bookingDetails?.user_details?.id,
      petId: bookingDetails?.pet_details?.id,
      bookingId: bookingDetails?.booking_id,
      price: 2222,
    });
    navigate(
      `${getRouteKey("AVAILABLE_SLOTS", "url")}/${bookingDetails?.user_id}/${bookingDetails?.pet_id}?type=reschedule&rbi=${bookingDetails?.booking_id}&source=rota_module`,
    );
  };

  const handleCancelAppointment = (bookingDetails: any) => {
    setShow();
    setModal({
      appointmentShiftDetails: bookingDetails,
      from: "ROTA",
    } as IBookingCancelationModalState);
  };

  const getPetSpecies = (bookingDetails: any) => {
    const speciesId = bookingDetails?.pet_details?.pet_species_type_id;
    if (speciesId === "1") return "Dog";
    if (speciesId === "2") return "Cat";
    return "NA";
  };

  const renderStatus = (bookingDetails: any) => (
    <BookingStatus
      bookingStatus={
        bookingDetails?.booking_status?.toLowerCase() === "pending" ? "Upcoming" : bookingDetails?.booking_status
      }
    />
  );

  const renderActions = (bookingDetails: any) => (
    <div className="flex gap-2 items-center">
      {!isFrontDeskOverbooking && (
        <button
          className="bg-black px-4 py-2 rounded-lg text-[10px] sm:text-xs text-white font-medium whitespace-nowrap hover:bg-gray-800 transition"
          onClick={() => handleModifyAppointment(bookingDetails)}
        >
          Modify
        </button>
      )}
      <button
        className="px-4 py-2 rounded-lg text-[10px] sm:text-xs text-[#E11D48] font-medium whitespace-nowrap border border-[#E11D48] hover:bg-[#E11D48] hover:text-white transition"
        onClick={() => handleCancelAppointment(bookingDetails)}
      >
        Cancel
      </button>
    </div>
  );

  const columns: TableColumn<any>[] = useMemo(
    () => [
      {
        id: "serial",
        header: "S.NO",
        width: 60,
        cell: ({ rowIndex }) => <span className="text-[#6C727F]">{rowIndex + 1}</span>,
      },
      {
        id: "status",
        header: "Status",
        minWidth: 110,
        cell: ({ row }) => renderStatus(row),
      },
      {
        id: "time",
        header: "Time",
        minWidth: 140,
        className: "text-[#6C727F]",
        cell: ({ row }) => veticDateFormat(row?.booking_start_date_time),
      },
      {
        id: "client",
        header: "Client Details",
        minWidth: 160,
        cell: ({ row }) => (
          <Username username={row?.user_details?.name} petId={row?.pet_details?.id} userId={row?.user_details?.id} />
        ),
      },
      {
        id: "pet",
        header: "Pet Details",
        minWidth: 140,
        cell: ({ row }) => (
          <div className="flex flex-col gap-1">
            <PetName petName={row?.pet_details?.name} className="text-xs text-[#0068FF]" petStatus="" />
            <span className="text-[#808DA0] text-xs">{getPetSpecies(row)}</span>
          </div>
        ),
      },
      {
        id: "service",
        header: "Service",
        minWidth: 150,
        cell: ({ row }) => (
          <div className="flex flex-col">
            <span>{row?.service_details?.service_type}</span>
            <span className="text-[#808DA0] mt-1">{row?.subservice_details?.name}</span>
          </div>
        ),
      },
      {
        id: "provider",
        header: "Provider",
        minWidth: 140,
        cell: ({ row }) => getDisplayStaffName(row) || "NA",
      },
      {
        id: "clinic",
        header: "Clinic Name",
        minWidth: 150,
        cell: ({ row }) => row?.clinic_details?.display_name,
      },
      {
        id: "created_by",
        header: "Created By",
        minWidth: 130,
        className: "text-[#808DA0]",
        cell: ({ row }) => row?.created_by || "NA",
      },
      {
        id: "actions",
        header: "Actions",
        minWidth: 170,
        cell: ({ row }) => renderActions(row),
      },
    ],
    [veticDateFormat, getDisplayStaffName],
  );

  return (
    <div>
      <UnifiedFilters
        formHook={formHook}
        cities={cities}
        clinics={clinics}
        services={orgServices}
        quickFilterCounts={shiftAppointmentCount}
        onCityChange={handleCityChange}
        onClinicChange={handleClinicChange}
        onServiceChange={handleServiceChange}
        onSubServiceChange={handleSubServiceChange}
        onDateRangeChange={handleDateRangeChange}
        onCustomDateRangeChange={handleCustomDateRangeChange}
        onApplyFilters={handleApplyFilters}
        resetClinicOnCityChange={true}
        initialDateRange={format(new Date(), "yyyy-MM-dd")}
      />

      {isShiftListLoading ? (
        <div className="w-full mt-4 ml-2">
          <TableSkeletonComponent columnLength={5} />
        </div>
      ) : (
        <div className="w-full pb-4 px-2 sm:px-3 mt-4">
          {shiftAppoitnemt?.length > 0 ? (
            <TableComponent
              columns={columns}
              data={shiftAppoitnemt}
              getRowId={(cb: any, rowIndex) => `${cb?.booking_id}:${rowIndex}`}
              rowClassName={() => "bg-white hover:bg-gray-50 transition"}
              scrollContainerClassName="max-h-[calc(100vh-280px)] overflow-y-auto overflow-x-auto"
              tableClassName="rounded-[20px]"
              bodyClassName="text-xs"
            />
          ) : (
            <div className="bg-white rounded-xl shadow-sm min-h-[320px] md:min-h-[400px] flex items-center justify-center">
              <NORecordFoundComponent imageURL={NO_PWL_RESULTS} message="No Active Shift Listed" />
            </div>
          )}
        </div>
      )}
    </div>
  );
};

export default AppointmentShiftAppointment;
